import React, { useEffect, useRef, useState } from 'react';
import { TEMA } from '../theme';

export interface Alternativ {
  värde: string;
  etikett: string;
  /** Dämpad text efter etiketten, t.ex. stad eller kundnummer. */
  under?: string;
}

interface Props {
  alternativ: Alternativ[];
  värde: string;
  onChange: (värde: string) => void;
  /** Texten när inget är valt. Fungerar också som "inget valt"-alternativ. */
  placeholder?: string;
  /** Sätt false för att kräva ett val. */
  tomTillåten?: boolean;
  /** Sökfältet visas först när listan är längre än så här. */
  sökGräns?: number;
  style?: React.CSSProperties;
  disabled?: boolean;
  autoFokus?: boolean;
}

const MAX_TRÄFFAR = 50;

// "château" ska hitta "Chateau": artiklar skrivs sällan med accenter.
function utanAccenter(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * En lista man kan skriva i. Ersätter <select> genom hela appen, så att
 * "Postgatan" hittas utan att man scrollar genom 658 kunder.
 *
 * Matchar var som helst i texten, inte bara från början som en vanlig
 * rullgardinsmeny gör.
 */
export default function SokbarLista({
  alternativ, värde, onChange, placeholder = 'Välj...', tomTillåten = true,
  sökGräns = 8, style, disabled, autoFokus,
}: Props) {
  const [öppen, setÖppen] = useState(false);
  const [sök, setSök] = useState('');
  const [markerad, setMarkerad] = useState(0);
  const ramRef = useRef<HTMLDivElement>(null);
  const fältRef = useRef<HTMLInputElement>(null);

  const valt = alternativ.find(a => a.värde === värde);
  const sökbar = alternativ.length > sökGräns;

  const träffar = (() => {
    const s = utanAccenter(sök.trim());
    if (!s) return alternativ.slice(0, MAX_TRÄFFAR);
    return alternativ
      .filter(a => utanAccenter(`${a.etikett} ${a.under || ''}`).includes(s))
      .slice(0, MAX_TRÄFFAR);
  })();

  useEffect(() => {
    if (öppen && sökbar) fältRef.current?.focus();
  }, [öppen, sökbar]);

  useEffect(() => { setMarkerad(0); }, [sök]);

  function välj(nyttVärde: string) {
    onChange(nyttVärde);
    setSök('');
    setÖppen(false);
  }

  function tangent(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setMarkerad(i => Math.min(i + 1, träffar.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setMarkerad(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && träffar[markerad]) { e.preventDefault(); välj(träffar[markerad].värde); }
    if (e.key === 'Escape') { setSök(''); setÖppen(false); }
  }

  const fältStil: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    paddingRight: '28px',
    fontSize: '14px',
    fontFamily: "'Inter', sans-serif",
    borderRadius: '2px',
    border: `1px solid ${TEMA.border}`,
    background: TEMA.bg,
    color: valt ? TEMA.ink : TEMA.inkFaint,
    boxSizing: 'border-box',
    outline: 'none',
    textAlign: 'left',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    ...style,
  };

  return (
    <div
      ref={ramRef}
      style={{ position: 'relative' }}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setSök('');
          setÖppen(false);
        }
      }}
    >
      {öppen && sökbar ? (
        <input
          ref={fältRef}
          type="text"
          value={sök}
          onChange={e => { setSök(e.target.value); setÖppen(true); }}
          onKeyDown={tangent}
          placeholder={valt ? valt.etikett : placeholder}
          style={{ ...fältStil, color: TEMA.ink, cursor: 'text' }}
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          autoFocus={autoFokus}
          onClick={() => setÖppen(o => !o)}
          onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); setÖppen(true); } }}
          style={fältStil}
        >
          {valt ? valt.etikett : placeholder}
        </button>
      )}

      <span aria-hidden="true" style={{
        position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
        pointerEvents: 'none', color: TEMA.inkFaint, fontSize: '10px',
      }}>
        ▾
      </span>

      {öppen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 30,
          maxHeight: '260px', overflowY: 'auto',
          background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '4px',
          boxShadow: '0 12px 28px rgba(0,0,0,0.45)',
        }}>
          {tomTillåten && !sök.trim() && (
            <button
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => välj('')}
              style={radStil(!värde, false)}
            >
              <span style={{ color: TEMA.inkFaint }}>{placeholder}</span>
            </button>
          )}

          {träffar.length === 0 && (
            <p style={{ margin: 0, padding: '12px 14px', fontSize: '13px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
              Inget matchar sökningen.
            </p>
          )}

          {träffar.map((a, i) => (
            <button
              key={a.värde}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => välj(a.värde)}
              onMouseEnter={() => setMarkerad(i)}
              style={radStil(a.värde === värde, i === markerad)}
            >
              {a.etikett}
              {a.under && <span style={{ color: TEMA.inkFaint }}> · {a.under}</span>}
            </button>
          ))}

          {alternativ.length > MAX_TRÄFFAR && !sök.trim() && (
            <p style={{ margin: 0, padding: '10px 14px', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
              Visar {MAX_TRÄFFAR} av {alternativ.length}. Skriv för att söka.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function radStil(vald: boolean, markerad: boolean): React.CSSProperties {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '9px 14px',
    border: 'none',
    cursor: 'pointer',
    background: markerad || vald ? TEMA.bg : 'transparent',
    color: TEMA.ink,
    fontFamily: "'Inter', sans-serif",
    fontSize: '13.5px',
  };
}
